<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎵 Note with Fingering Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Note Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Note Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="note-input">Note:</label>
                            <input
                                id="note-input"
                                v-model="noteValue"
                                type="text"
                                placeholder="e.g., C4, D#5, Bb4"
                                class="text-input"
                            />
                            <div class="note-hint text-caption text-medium-emphasis">
                                Standard fingerings available for: C4-C7
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Clef Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Clef:</label>
                            <div class="button-group">
                                <button
                                    @click="setClef('treble')"
                                    :class="{ active: clefValue === 'treble' }"
                                    class="state-btn"
                                >
                                    Treble
                                </button>
                                <button
                                    @click="setClef('bass')"
                                    :class="{ active: clefValue === 'bass' }"
                                    class="state-btn"
                                >
                                    Bass
                                </button>
                                <button
                                    @click="setClef('alto')"
                                    :class="{ active: clefValue === 'alto' }"
                                    class="state-btn"
                                >
                                    Alto
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Duration Selection</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Duration:</label>
                            <div class="button-group-vertical">
                                <button
                                    @click="setDuration('whole')"
                                    :class="{
                                        active: durationValue === 'whole',
                                    }"
                                    class="state-btn"
                                >
                                    Whole
                                </button>
                                <button
                                    @click="setDuration('half')"
                                    :class="{ active: durationValue === 'half' }"
                                    class="state-btn"
                                >
                                    Half
                                </button>
                                <button
                                    @click="setDuration('quarter')"
                                    :class="{
                                        active: durationValue === 'quarter',
                                    }"
                                    class="state-btn"
                                >
                                    Quarter
                                </button>
                                <button
                                    @click="setDuration('eighth')"
                                    :class="{
                                        active: durationValue === 'eighth',
                                    }"
                                    class="state-btn"
                                >
                                    Eighth
                                </button>
                                <button
                                    @click="setDuration('sixteenth')"
                                    :class="{
                                        active: durationValue === 'sixteenth',
                                    }"
                                    class="state-btn"
                                >
                                    Sixteenth
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Display Options</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showNoteLabel"
                                />
                                Show note label under fingering
                            </label>
                        </div>
                    </div>
                </div>

                <div class="actions">
                    <button @click="setExample" class="action-btn fill">
                        Reset to Default
                    </button>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Preview</h2>
                <div class="preview-box">
                    <NoteWithFingering
                        :note="activeNote"
                        :clef="activeClef"
                        :duration="activeDuration"
                        :showNoteLabel="showNoteLabel"
                        :noteWidth="noteWidth"
                        :noteHeight="noteHeight"
                        :fingeringWidth="fingeringWidth"
                        :fingeringHeight="fingeringHeight"
                    />
                </div>

                <div class="size-controls bg-surface-light">
                    <h3 class="text-h6">Music Note Size</h3>
                    <div class="size-control-group">
                        <label for="note-width-slider"
                            >Width: {{ noteWidth }}px</label
                        >
                        <input
                            id="note-width-slider"
                            type="range"
                            v-model.number="noteWidth"
                            min="150"
                            max="400"
                            step="25"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="note-height-slider"
                            >Height: {{ noteHeight }}px</label
                        >
                        <input
                            id="note-height-slider"
                            type="range"
                            v-model.number="noteHeight"
                            min="80"
                            max="200"
                            step="20"
                        />
                    </div>

                    <h3 class="text-h6">Fingering Diagram Size</h3>
                    <div class="size-control-group">
                        <label for="fingering-width-slider"
                            >Width: {{ fingeringWidth }}px</label
                        >
                        <input
                            id="fingering-width-slider"
                            type="range"
                            v-model.number="fingeringWidth"
                            min="300"
                            max="700"
                            step="50"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="fingering-height-slider"
                            >Height: {{ fingeringHeight }}px</label
                        >
                        <input
                            id="fingering-height-slider"
                            type="range"
                            v-model.number="fingeringHeight"
                            min="120"
                            max="300"
                            step="20"
                        />
                    </div>
                </div>

                <div class="preview-info bg-surface-light">
                    <h3 class="text-h6">Current Props:</h3>
                    <div class="prop-display">
                        <div><strong>note:</strong> "{{ activeNote }}"</div>
                        <div><strong>clef:</strong> "{{ activeClef }}"</div>
                        <div>
                            <strong>duration:</strong> "{{ activeDuration }}"
                        </div>
                        <div>
                            <strong>showNoteLabel:</strong>
                            {{ showNoteLabel }}
                        </div>
                        <div>
                            <strong>fingering:</strong> {{ fingeringStatus }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Examples Section -->
        <div class="examples-section">
            <h2 class="text-h5 text-secondary">Common Note Examples</h2>
            <div class="examples-grid">
                <div
                    v-for="example in examples"
                    :key="example.name"
                    class="example-card"
                >
                    <h3>{{ example.name }}</h3>
                    <NoteWithFingering
                        :note="example.note"
                        :clef="example.clef || 'treble'"
                        :duration="example.duration || 'quarter'"
                        :noteWidth="180"
                        :noteHeight="100"
                        :fingeringWidth="400"
                        :fingeringHeight="140"
                        :showNoteLabel="true"
                    />
                    <button @click="loadExample(example)" class="load-btn">
                        Load
                    </button>
                </div>
            </div>
        </div>

        <!-- Code Output -->
        <div class="code-section">
            <h2>Component Code</h2>
            <pre><code>{{ componentCode }}</code></pre>
            <button @click="copyCode" class="copy-btn">📋 Copy</button>
        </div>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Props</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Prop</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>note</code></td>
                            <td><code>string</code></td>
                            <td>"C5"</td>
                            <td>Note to display (e.g., "C4", "F#5", "Bb4")</td>
                        </tr>
                        <tr>
                            <td><code>clef</code></td>
                            <td><code>string</code></td>
                            <td>"treble"</td>
                            <td>Musical clef (treble, bass, alto, tenor)</td>
                        </tr>
                        <tr>
                            <td><code>duration</code></td>
                            <td><code>string</code></td>
                            <td>"quarter"</td>
                            <td>Note duration (whole, half, quarter, eighth, sixteenth)</td>
                        </tr>
                        <tr>
                            <td><code>noteWidth</code></td>
                            <td><code>number</code></td>
                            <td>200</td>
                            <td>Width of music note display in pixels</td>
                        </tr>
                        <tr>
                            <td><code>noteHeight</code></td>
                            <td><code>number</code></td>
                            <td>120</td>
                            <td>Height of music note display in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingeringWidth</code></td>
                            <td><code>number</code></td>
                            <td>500</td>
                            <td>Width of fingering diagram in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingeringHeight</code></td>
                            <td><code>number</code></td>
                            <td>180</td>
                            <td>Height of fingering diagram in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingering</code></td>
                            <td><code>FingeringState</code></td>
                            <td>auto</td>
                            <td>Custom fingering state, or auto-detected from note</td>
                        </tr>
                        <tr>
                            <td><code>showNoteLabel</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Show note label under fingering diagram</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Features</h3>
                <ul class="api-list">
                    <li><strong>Combined display:</strong> Shows both musical notation and fingering</li>
                    <li><strong>Auto-fingering:</strong> Automatically looks up standard fingering for notes</li>
                    <li><strong>Flexible layout:</strong> Vertical stack with note on top, fingering below</li>
                    <li><strong>Accidentals supported:</strong> Handles sharps and flats</li>
                    <li><strong>Range: C4-C7:</strong> Covers full flute range with standard fingerings</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Usage Example</h3>
                <pre class="usage-example"><code>&lt;NoteWithFingering
  note="C5"
  clef="treble"
  duration="quarter"
  :show-note-label="true"
/&gt;</code></pre>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import NoteWithFingering from "../components/NoteWithFingering.vue";
import { hasStandardFingering } from "../data/fluteFingeringChart";

interface Example {
    name: string;
    note: string;
    clef?: string;
    duration?: string;
}

const noteValue = ref("C5");
const clefValue = ref("treble");
const durationValue = ref("quarter");
const showNoteLabel = ref(true);
const noteWidth = ref(200);
const noteHeight = ref(120);
const fingeringWidth = ref(500);
const fingeringHeight = ref(180);

const activeNote = computed(() => noteValue.value);
const activeClef = computed(() => clefValue.value);
const activeDuration = computed(() => durationValue.value);

const fingeringStatus = computed(() => {
    const note = activeNote.value;
    if (hasStandardFingering(note)) {
        return `Standard fingering for ${note}`;
    }
    return "No standard fingering available";
});

const componentCode = computed(() => {
    const props: string[] = [];

    props.push(`  note="${activeNote.value}"`);
    props.push(`  clef="${activeClef.value}"`);
    props.push(`  duration="${activeDuration.value}"`);

    if (noteWidth.value !== 200) {
        props.push(`  :noteWidth="${noteWidth.value}"`);
    }
    if (noteHeight.value !== 120) {
        props.push(`  :noteHeight="${noteHeight.value}"`);
    }
    if (fingeringWidth.value !== 500) {
        props.push(`  :fingeringWidth="${fingeringWidth.value}"`);
    }
    if (fingeringHeight.value !== 180) {
        props.push(`  :fingeringHeight="${fingeringHeight.value}"`);
    }
    if (!showNoteLabel.value) {
        props.push(`  :showNoteLabel="false"`);
    }

    const propsString = props.length > 0 ? "\n" + props.join("\n") + "\n" : "";

    return `<NoteWithFingering${propsString}/>`;
});

function setClef(clef: string) {
    clefValue.value = clef;
}

function setDuration(duration: string) {
    durationValue.value = duration;
}

function setExample() {
    noteValue.value = "C5";
    clefValue.value = "treble";
    durationValue.value = "quarter";
    showNoteLabel.value = true;
}

const examples: Example[] = [
    {
        name: "Middle C",
        note: "C5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "D5",
        note: "D5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "E5",
        note: "E5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "F5",
        note: "F5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "G5",
        note: "G5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "A5",
        note: "A5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "B5",
        note: "B5",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "High C",
        note: "C6",
        clef: "treble",
        duration: "quarter",
    },
    {
        name: "F# (Half Note)",
        note: "F#5",
        clef: "treble",
        duration: "half",
    },
    {
        name: "Bb (Eighth)",
        note: "Bb5",
        clef: "treble",
        duration: "eighth",
    },
];

function loadExample(example: Example) {
    noteValue.value = example.note;
    clefValue.value = example.clef || "treble";
    durationValue.value = example.duration || "quarter";
}

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}
</script>

<style scoped>
.debug-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.checkbox-label {
    display: flex !important;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
}

.text-input {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
}

.text-input:focus {
    outline: none;
}

.note-hint {
    font-size: 12px;
    font-style: italic;
    opacity: 0.7;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.button-group-vertical {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    flex: 1;
    text-align: center;
    font-weight: 500;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    padding-top: 20px;
}

.action-btn {
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn.fill:hover {
    opacity: 0.9;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    border-radius: 6px;
    min-height: 400px;
}

.size-controls {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.size-controls label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 14px;
}

.size-control-group {
    margin-bottom: 20px;
}

.size-controls input[type="range"] {
    width: 100%;
    margin-bottom: 0;
}

.preview-info {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.prop-display {
    margin-top: 10px;
}

.prop-display div {
    margin-bottom: 5px;
    font-family: monospace;
    font-size: 14px;
    padding: 8px;
    border-radius: 3px;
    font-weight: 500;
}

.examples-section {
    margin: 40px 0;
}

.examples-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
    gap: 20px;
    margin-top: 15px;
}

.example-card {
    border-radius: 8px;
    padding: 15px;
    text-align: center;
    transition: all 0.2s;
}

.example-card:hover {
    transform: translateY(-2px);
}

.example-card h3 {
    margin: 0 0 10px 0;
    font-size: 16px;
}

.load-btn {
    margin-top: 10px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.load-btn:hover {
    opacity: 0.9;
}

.code-section {
    position: relative;
    padding: 20px;
    border-radius: 8px;
    margin-top: 40px;
}

.code-section h2 {
    margin-top: 0;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.copy-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.copy-btn:hover {
    opacity: 0.9;
}

@media (max-width: 1200px) {
    .examples-grid {
        grid-template-columns: 1fr;
    }
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

.usage-example {
    background: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
}

.usage-example code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
